import { useTranslation } from "@sagot/i18n";
import { Badge, ICONS, Icon, StatusBadge, Switch, cn, type IconName, type StatusKind } from "@sagot/ui";
import type { Action } from "../shared/types";
import { bookingModeOf, type BookingMode } from "./availability-model";
import { RoutingLine } from "./routing-line";
import { builtinBadge } from "./schema-model";

const TOOL_ICONS: Readonly<Record<string, IconName>> = {
  capture_lead: "user-check",
  human_handoff: "headset",
  send_checklist_email: "mail",
};
const INTEGRATION_ICONS: Readonly<Record<string, IconName>> = { custom_api: "code", webhook: "webhook", zapier: "workflow" };

/** Booking cards say what visitors get: icon + words + colour. */
const BOOKING_STATUS: Readonly<Record<BookingMode, { status: StatusKind; icon: IconName }>> = {
  calendar: { status: "success", icon: "calendar" },
  request: { status: "info", icon: "user-check" },
  unconfigured: { status: "warning", icon: "alert-triangle" },
};

function iconOf(action: Action): IconName {
  if (action.icon && action.icon in ICONS) return action.icon as IconName;
  if (action.type === "builtin") return TOOL_ICONS[action.tool_name] ?? (action.tool_name.startsWith("book_") ? "calendar" : "zap");
  return INTEGRATION_ICONS[action.integration ?? ""] ?? "code";
}

interface ActionCardProps {
  action: Action;
  canToggle: boolean;
  toggling: boolean;
  onOpen: (action: Action) => void;
  onToggle: (action: Action) => void;
}

/** Grid card: opens the editor on click/Enter; the inline switch only toggles `enabled`. */
export function ActionCard({ action, canToggle, toggling, onOpen, onToggle }: ActionCardProps) {
  const { t } = useTranslation();
  const titleId = `action-${action.id}`;
  const bookingMode = bookingModeOf(action);
  const kind = action.type === "builtin"
    ? { tone: "brand" as const, label: t(`build.actions.card.${builtinBadge(action.tool_name)}`) }
    : { tone: "neutral" as const, label: t(`build.actions.card.integration.${action.integration ?? "custom_api"}`, { defaultValue: action.integration ?? "" }) };
  return (
    <article className="card relative flex flex-col p-5 hover:border-line-strong" data-testid="action-card" aria-labelledby={titleId}>
      <div className="flex items-start justify-between gap-3">
        <span className={cn("inline-flex size-10 items-center justify-center rounded-lg", action.enabled ? "bg-primary-soft text-primary-ink" : "bg-sunken text-fg-subtle")}>
          <Icon name={iconOf(action)} className="size-5" />
        </span>
        <div className="relative z-10 flex items-center gap-2">
          {action.locked ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-fg-subtle" data-testid="always-on">
              <Icon name="lock" className="size-3" />
              {t("build.actions.card.alwaysOn")}
            </span>
          ) : null}
          <Switch
            checked={action.enabled}
            disabled={!canToggle || toggling || action.locked}
            onCheckedChange={() => { onToggle(action); }}
            aria-label={t(action.locked ? "build.actions.card.toggleLocked" : "build.actions.card.toggle", { name: action.name })}
          />
        </div>
      </div>
      <h3 id={titleId} className="mt-3 text-sm font-bold">
        <button type="button" className="text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:rounded-card focus-visible:after:ring-2 focus-visible:after:ring-primary" onClick={() => { onOpen(action); }}>
          {action.name}
        </button>
      </h3>
      <p className="mt-1 line-clamp-3 flex-1 text-xs text-fg-muted">{action.when_to_use}</p>
      {action.routing ? <RoutingLine routing={action.routing} agentId={action.agent_id} /> : null}
      {bookingMode ? (
        <div className="mt-3">
          <StatusBadge data-testid="booking-status" {...BOOKING_STATUS[bookingMode]} label={t(`build.actions.card.booking.${bookingMode}`)} />
        </div>
      ) : null}
      {action.requires_identity ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge tone="warning">
            <Icon name="lock" className="size-3" />
            {t("build.actions.card.requiresIdentity")}
          </Badge>
        </div>
      ) : null}
      <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-xs">
        <Badge tone={kind.tone}>{kind.label}</Badge>
        {action.enabled ? (
          <span className="font-semibold tabular-nums text-fg-muted">{t("build.actions.card.runs", { count: action.runs_7d })}</span>
        ) : (
          <span className="font-semibold text-fg-subtle">{t("build.actions.card.off")}</span>
        )}
      </div>
    </article>
  );
}
